import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { Box, Card, Collapse, DialogContent, Grid, Skeleton, Toolbar } from "@mui/material";
import styles from "./styles.module.scss";
import { AlbumImageCard } from "../AlbumImageCard";
import { useGetAlbumImages } from "../../../../hooks/albumImages/useGetAlbumImages";
import { IAlbum } from "../../../../hooks/albums";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { useIsMobile } from "../../../../hooks/isMobile";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { strings } from "../../../../localization/LocalizedStrings";
import { IAlbumImage, IAlbumImagesResponse } from "../../../../hooks/albumImages";
import { useAddAlbumImage } from "../../../../hooks/albumImages/useAddAlbumImage";
import { AlertDialog } from "../../../AlertDialog";
import { toast } from "react-toastify";
import { arrayMoveImmutable } from "array-move";
import { useReorderAlbumImages } from "../../../../hooks/albumImages/useReorderAlbumImages";
import { useGetPublicAlbumImages } from "../../../../hooks/albumImages/useGetPublicAlbumImages";
import { useCurrentPublicCollection } from "../../../../hooks/collections/useCollectionManager";
import { RoutesBuilder } from "../../../../models/RoutesBuilder";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import {
  Album3DRepresentationContainer,
  Album3DRepresentationContainerHandle
} from "../../Album3DRepresentation/Container";
import { flushSync } from "react-dom";

export const ImageGalleryDialog: FunctionComponent<IImageGalleryDialogProps> = ({
  album,
  isPublic,
  isOpen,
  setIsOpen,
  onAlbumImageUpdated,
  onAdditionalAlbumImageUpdated,
  onOpenImageDetail
}) => {
  const isMobile = useIsMobile();
  const { getAlbumImages } = useGetAlbumImages();
  const { getPublicAlbumImages } = useGetPublicAlbumImages();
  const { addAlbumImage } = useAddAlbumImage();
  const { reorderAlbumImages } = useReorderAlbumImages();
  const [currentPublicCollection] = useCurrentPublicCollection.useState();
  const [frontAlbumImage, setFrontAlbumImage] = useState<IAlbumImage>();
  const [albumImages, setAlbumImages] = useState<IAlbumImagesResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isLoadingFrontImage, setIsLoadingFrontImage] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const bottomReference = useRef<HTMLHeadingElement>(null);
  const [isThreeDimensionalPanelExpanded, setIsThreeDimensionalPanelExpanded] = useState(false);
  const genericSleeveImage = new URL("../../../../images/generic_sleeve.jpg", import.meta.url).href;
  const genericBackImage = new URL("../../../../images/generic_back.jpg", import.meta.url).href;
  const genericSpineImage = new URL("../../../../images/generic_spine.png", import.meta.url).href;
  const scrollToBottom = () => {
    bottomReference.current?.scrollIntoView({ behavior: "smooth" });
  };
  const fetchAlbumImages = async () => {
    setIsLoading(true);
    setAlbumImages(undefined);
    const { status, body } = await getAlbumImages({ albumId: album.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      setAlbumImages(body);
    } else {
      toast.error(body.message);
    }
    setIsLoading(false);
  };
  const fetchPublicAlbumImages = async () => {
    if (!currentPublicCollection) return;
    setIsLoading(true);
    setAlbumImages(undefined);
    const { status, body } = await getPublicAlbumImages({
      albumId: album.uuid,
      collectionId: currentPublicCollection.uuid
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      setAlbumImages(body);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (!isOpen) return;
    if (isPublic) {
      fetchPublicAlbumImages().then();
    } else {
      fetchAlbumImages().then();
    }
  }, [isOpen]);
  useEffect(() => {
    setFrontAlbumImage({
      uuid: album.uuid,
      kind: "additional",
      description: "",
      thumbnail: album.thumbnail,
      hasImage: album.hasImage
    });
  }, [album.uuid, album.hasImage, album.thumbnail]);
  const addNewAlbumImage = async () => {
    if (!albumImages) return;
    setIsAddLoading(true);
    const { status, body } = await addAlbumImage({
      albumId: album.uuid,
      kind: "additional",
      description: "",
      order: albumImages.additional.length
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      flushSync(() => {
        setAlbumImages(albumImages => {
          if (!albumImages) return;
          return {
            ...albumImages,
            additional: [...albumImages.additional, body]
          };
        });
      });
      scrollToBottom();
      toast.success(strings.generic_update_success);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsAddLoading(false);
  };
  const reorderImages = async (newlyOrderedAlbumImages: IAlbumImage[]) => {
    setIsAddLoading(true);
    const { status, body } = await reorderAlbumImages(newlyOrderedAlbumImages);
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.generic_update_success);
      setAlbumImages(albumImages => {
        if (!albumImages) return;
        return {
          ...albumImages,
          additional: newlyOrderedAlbumImages
        };
      });
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsAddLoading(false);
  };
  const album3DRepresentationRef = useRef<Album3DRepresentationContainerHandle>(null);
  return (
    <PiperDialog fullWidth maxWidth={"lg"} open={isOpen} onClose={() => setIsOpen(false)}>
      <Toolbar>
        <h3 style={{ marginRight: 8 }}>{strings.image_gallery}</h3>
        {!isPublic && albumImages && (
          <ToolbarButton
            tooltip={strings.add_new_image}
            iconType={IconType.AddCircle}
            disabled={isAddLoading || albumImages.additional.length >= 50}
            loading={isAddLoading}
            onClick={addNewAlbumImage}
          />
        )}
        {albumImages && (
          <ToolbarButton
            tooltip={strings.album_3d_model}
            iconType={IconType.ThreeDimensionalRotation}
            highlighted={isThreeDimensionalPanelExpanded}
            onClick={() => setIsThreeDimensionalPanelExpanded(!isThreeDimensionalPanelExpanded)}
          />
        )}
        <Box flexGrow={1} />
        <ToolbarButton
          href={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.imageGallery()}`}
          target={"_blank"}
          tooltip={strings.help}
          iconType={IconType.Help}
        />
        <ToolbarButton
          onClick={() => setIsOpen(false)}
          tooltip={strings.close}
          iconType={IconType.Close}
          loading={isLoading}
        />
      </Toolbar>
      <DialogContent>
        {albumImages && (
          <Collapse in={isThreeDimensionalPanelExpanded} timeout={"auto"}>
            <Card className={styles.threeDimensionalPanel}>
              <Album3DRepresentationContainer
                ref={album3DRepresentationRef}
                frontCover={frontAlbumImage}
                backCover={albumImages.back[0]}
                spine={albumImages.spine[0]}
                size={320}
                initialRotation={28}
                isLoadingFrontCover={isLoadingFrontImage}
                setIsLoadingFrontCover={setIsLoadingFrontImage}
              />
            </Card>
          </Collapse>
        )}
        <Card sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            {isLoading ? (
              Array.from({ length: 3 }, (_value, index) => (
                <Grid key={index} item xs={isMobile ? 12 : 12 / 4} className={styles.item}>
                  <Skeleton variant={"rectangular"} className={styles.skeleton} />
                </Grid>
              ))
            ) : (
              <>
                <Grid key={"front"} item xs={isMobile ? 12 : 12 / 4}>
                  <AlbumImageCard
                    isPublic={isPublic}
                    album={album}
                    albumImage={frontAlbumImage}
                    genericImage={genericSleeveImage}
                    kind={"front"}
                    description={strings.front_cover}
                    index={-1}
                    onAlbumImageUploaded={albumImage => {
                      setFrontAlbumImage(albumImage);
                      onAlbumImageUpdated(albumImage);
                    }}
                    onOpenImageDetail={onOpenImageDetail}
                    canMoveLeft={false}
                    canMoveRight={false}
                    setIsLoading={setIsLoadingFrontImage}
                  />
                </Grid>
                {(!isPublic || albumImages?.back[0]) && (
                  <Grid key={"back"} item xs={isMobile ? 12 : 12 / 4}>
                    <AlbumImageCard
                      isPublic={isPublic}
                      album={album}
                      albumImage={albumImages?.back[0]}
                      genericImage={genericBackImage}
                      kind={"back"}
                      description={strings.back_cover}
                      index={-1}
                      onAlbumImageUploaded={albumImage => {
                        setAlbumImages(albumImages => {
                          if (!albumImages) return;
                          return {
                            ...albumImages,
                            back: [albumImage]
                          };
                        });
                        album3DRepresentationRef.current?.refreshImages();
                        onAdditionalAlbumImageUpdated();
                      }}
                      onOpenImageDetail={onOpenImageDetail}
                      canMoveLeft={false}
                      canMoveRight={false}
                    />
                  </Grid>
                )}
                {(!isPublic || albumImages?.spine[0]) && (
                  <Grid key={"spine"} item xs={isMobile ? 12 : 12 / 4}>
                    <AlbumImageCard
                      isPublic={isPublic}
                      album={album}
                      albumImage={albumImages?.spine[0]}
                      genericImage={genericSpineImage}
                      kind={"spine"}
                      description={strings.spine}
                      index={-1}
                      onAlbumImageUploaded={albumImage => {
                        setAlbumImages(albumImages => {
                          if (!albumImages) return;
                          return {
                            ...albumImages,
                            spine: [albumImage]
                          };
                        });
                        album3DRepresentationRef.current?.refreshImages();
                        onAdditionalAlbumImageUpdated();
                      }}
                      onOpenImageDetail={onOpenImageDetail}
                      canMoveLeft={false}
                      canMoveRight={false}
                    />
                  </Grid>
                )}
                {albumImages &&
                  albumImages.additional.length > 0 &&
                  albumImages.additional.map((albumImage, index) => (
                    <Grid key={index} item xs={isMobile ? 12 : 12 / 4}>
                      <AlbumImageCard
                        isPublic={isPublic}
                        album={album}
                        albumImage={albumImage}
                        genericImage={genericSleeveImage}
                        kind={albumImage.kind}
                        description={albumImage.description}
                        index={index}
                        onAlbumImageUploaded={albumImage =>
                          setAlbumImages(albumImages => {
                            if (!albumImages) return;
                            const additionalImages = albumImages.additional.map(image => {
                              return image.uuid == albumImage.uuid ? albumImage : image;
                            });
                            return {
                              ...albumImages,
                              additional: additionalImages
                            };
                          })
                        }
                        onAlbumImageDeleted={() =>
                          setAlbumImages(albumImages => {
                            if (!albumImages) return;
                            return {
                              ...albumImages,
                              additional: albumImages.additional.filter(
                                image => image.uuid != albumImage.uuid
                              )
                            };
                          })
                        }
                        onOpenImageDetail={onOpenImageDetail}
                        canMoveLeft={index > 0}
                        canMoveRight={index < albumImages.additional.length - 1}
                        onMoveLeft={async () => {
                          const newlyOrderedAlbumImages = arrayMoveImmutable(
                            albumImages.additional,
                            index,
                            index - 1
                          );
                          await reorderImages(newlyOrderedAlbumImages);
                        }}
                        onMoveRight={async () => {
                          const newlyOrderedAlbumImages = arrayMoveImmutable(
                            albumImages.additional,
                            index,
                            index + 1
                          );
                          await reorderImages(newlyOrderedAlbumImages);
                        }}
                        isLoadingMove={isAddLoading}
                      />
                    </Grid>
                  ))}
              </>
            )}
            <div ref={bottomReference} />
          </Grid>
        </Card>
      </DialogContent>
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </PiperDialog>
  );
};

export interface IImageGalleryDialogProps {
  album: IAlbum;
  isPublic: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onAlbumImageUpdated: (albumImage: IAlbumImage) => void;
  onAdditionalAlbumImageUpdated: () => void;
  onOpenImageDetail: (id: string, image: string, title: string, filename: string) => void;
}
